type PendingRead = { path: string; start: () => void; priority: boolean };

// Private to one mounted job gallery: no persistent or cross-account cache.
export function createImageLoader(
  read: (path: string) => Promise<Blob>,
  concurrency = 4,
  maxCachedBytes = 24 * 1024 * 1024
) {
  const cache = new Map<string, Blob>();
  const inFlight = new Map<string, Promise<Blob>>();
  const pending: PendingRead[] = [];
  const queued = new Map<string, PendingRead>();
  let active = 0;
  let cachedBytes = 0;
  let generation = 0;

  function pump() {
    while (active < concurrency && pending.length > 0) {
      const priorityIndex = pending.findIndex((item) => item.priority);
      const [item] = pending.splice(priorityIndex < 0 ? 0 : priorityIndex, 1);
      queued.delete(item.path);
      active++;
      item.start();
    }
  }

  return {
    load(path: string, priority = false): Promise<Blob> {
      const cached = cache.get(path);
      if (cached) {
        cache.delete(path);
        cache.set(path, cached);
        return Promise.resolve(cached);
      }
      const running = inFlight.get(path);
      if (running) {
        // Already downloading, or already queued: a queued-but-not-started
        // entry can still be promoted to the front so a later foreground
        // request (e.g. opening the lightbox) doesn't wait behind it.
        if (priority) {
          const entry = queued.get(path);
          if (entry) entry.priority = true;
        }
        return running;
      }
      const requestGeneration = generation;
      const request = new Promise<Blob>((resolve, reject) => {
        const entry: PendingRead = { path, priority, start: () => {
          void Promise.resolve().then(() => read(path)).then((blob) => {
            if (generation === requestGeneration && blob.size <= maxCachedBytes) {
              while (cachedBytes + blob.size > maxCachedBytes && cache.size > 0) {
                const oldest = cache.keys().next().value!;
                cachedBytes -= cache.get(oldest)!.size;
                cache.delete(oldest);
              }
              cache.set(path, blob);
              cachedBytes += blob.size;
            }
            resolve(blob);
          }, reject).finally(() => {
            if (inFlight.get(path) === request) inFlight.delete(path);
            active--;
            pump();
          });
        } };
        pending.push(entry);
        queued.set(path, entry);
      });
      inFlight.set(path, request);
      pump();
      return request;
    },
    clear() {
      generation++;
      cache.clear();
      inFlight.clear();
      pending.length = 0;
      queued.clear();
      cachedBytes = 0;
    },
  };
}

export type ImageLoader = ReturnType<typeof createImageLoader>;
